import { Component, Inject, OnInit } from "@angular/core";

import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { AnalyticsService } from "@kells/apis/analytics";
import { PatientService } from "@kells/clinic-one/apis";
import { KellsPatient } from "@kells/interfaces/patient";
import { InfoCollectionOpenedEvent } from "apps/patient-reporting/src/app/shared/models/analytics/home-events";
import { format } from "date-fns";
import { PhoneNumberUtil } from "google-libphonenumber";
import {
  CountryISO,
  PhoneNumberFormat,
  SearchCountryField,
} from "ngx-intl-tel-input";
import { Country } from "ngx-intl-tel-input/lib/model/country.model";

const phoneUtil = PhoneNumberUtil.getInstance();

export type EssentialPatientInfoModalData =
  | KellsPatient
  | { patient: KellsPatient; confirmMode?: boolean };

@Component({
  selector: "kpr-essential-patient-info-modal",
  templateUrl: "./essential-patient-info-modal.component.html",
  styleUrls: ["./essential-patient-info-modal.component.scss"],
})
export class EssentialPatientInfoModalComponent implements OnInit {
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates];
  selectedCountry = CountryISO.UnitedStates;

  form: FormGroup = this.fb.group({
    firstName: ["", Validators.required],
    lastName: ["", Validators.required],
    birthday: ["", Validators.required],
    gender: ["", Validators.required],
    phone: "",
    state: "",
    zipcode: "",
    has_insurance: false,
    consent_sms: false,
  });

  constructor(
    private fb: FormBuilder,
    private patientService: PatientService,
    @Inject(MAT_DIALOG_DATA) public data: EssentialPatientInfoModalData,
    public dialogRef: MatDialogRef<EssentialPatientInfoModalComponent>,
    private analyticsService: AnalyticsService
  ) {}

  get confirmMode(): boolean {
    return (
      typeof this.data === "object" &&
      this.data !== null &&
      "confirmMode" in this.data &&
      !!this.data.confirmMode
    );
  }

  get patient(): KellsPatient {
    if (
      typeof this.data === "object" &&
      this.data !== null &&
      "patient" in this.data
    ) {
      return this.data.patient;
    }
    return this.data as KellsPatient;
  }

  onCountryChange(country: Country) {
    this.selectedCountry = country.iso2 as CountryISO;
  }

  ngOnInit(): void {
    this.analyticsService.record(InfoCollectionOpenedEvent({}));

    const patient = this.patient as KellsPatient & {
      first_name?: string;
      last_name?: string;
      dob?: string;
    };
    const phoneNumber =
      [patient.phone_country_code, patient.phone].filter(Boolean).join("") ?? "";
    if (patient.phone_country_code) {
      this.setCountryFromPhoneNumber(phoneNumber);
    }

    const rawBirthday = patient.birthday ?? patient.dob ?? "";
    // Strip trailing time component so local date parsing is consistent
    // e.g. "1990-05-15T00:00:00" → "1990-05-15"
    const normalizedBirthday = typeof rawBirthday === "string"
      ? rawBirthday.split("T")[0]
      : rawBirthday;
    const birthdayValue = normalizedBirthday ? new Date(`${normalizedBirthday}T12:00:00`) : "";
    const rawGender = patient.gender === "unknown" ? "" : patient.gender ?? "";
    const normalizedGender = rawGender.toLowerCase();

    this.form.patchValue({
      firstName: patient.firstName ?? patient.first_name ?? "",
      lastName: patient.lastName ?? patient.last_name ?? "",
      birthday: birthdayValue,
      gender: ["male", "female", "non-binary"].includes(normalizedGender)
        ? normalizedGender
        : rawGender,
      state: patient.state ?? "",
      zipcode: patient.zipcode ?? "",
      phone: phoneNumber,
      has_insurance: patient.has_insurance ?? false,
      consent_sms: patient.consent_sms ?? false,
    });
  }

  private setCountryFromPhoneNumber(phoneNumber: string) {
    try {
      const parsedNumber = phoneUtil.parse(phoneNumber);
      const regionCode = phoneUtil.getRegionCodeForNumber(parsedNumber);

      if (regionCode) {
        this.selectedCountry = regionCode as CountryISO;
      }
    } catch (error) {
      console.error("Error parsing phone number:", error);
    }
  }

  private _updateValueAndValidity() {
    for (const controlName in this.form.controls) {
      this.form.controls[controlName].updateValueAndValidity();
    }
  }

  onSubmit() {
    this._updateValueAndValidity();
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    const data = {
      id: this.patient.id,
      dob: format(new Date(this.form.value.birthday), "yyyy-MM-dd"),
      first_name: this.form.value.firstName,
      last_name: this.form.value.lastName,
      state: this.form.value.state,
      zipcode: this.form.value.zipcode,
      gender: this.form.value.gender,
      phone:
        this.form.value.phone?.e164Number
          ?.replace(this.form.value.phone?.dialCode ?? "", "")
          ?.replace(/\D+/g, "") ?? "",
      phone_country_code: this.form.value.phone?.dialCode ?? "",
      has_insurance: this.form.value.has_insurance ?? false,
      consent_sms: this.form.value.consent_sms ?? false,
    };

    this.patientService.updatePatient(data).subscribe(() => {
      this.dialogRef.close();
    });
  }
}
